import type React from 'react';
import { useContext } from 'react';
import { useDispatch } from 'react-redux';

import { Flex, Grid, Spacer } from '@zillow/constellation';
import { Button, Form, FormActions, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';
import { TransitionWrapper } from '@zillow/zrs-comet';

import { FreemiumContext } from '../../Freemium/context';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import actions from '../../../containers/CreateListingPage/actions';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

import RestrictedListingBanner from '../../RestrictedListingBanner';

import {
  StyledContentContainer,
  StyledDesktopSpacer,
  StyledExitCTA,
  StyledIconAwardRibbon,
  StyledMobileGrid,
  StyledSpan,
  StyledWizardContainer,
  StyledWizardTitle,
} from './StyledComponents';

export type PageWizardProps = {
  children: React.ReactNode;
  description?: React.ReactNode;
  isTransitioning?: boolean;
  wizardTitle?: React.ReactNode;
  subtitle?: React.ReactNode;
  title?: string;
  progressStepper: React.ReactNode;
  totalSteps?: number | null;
  currentStepIndex?: number;
  currentStepKey?: string;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  exitCTALabel?: string;
  hideDesktopWizardTitle?: boolean;
  hideExitCTA?: boolean;
  nextButtonType?: string;
  nextLabel?: string;
  backLabel?: string;
  onBackClick?: () => void;
  onNextClick?: () => void;
  onNextDataEventId?: string;
  onExitTriggered?: (...args: Array<unknown>) => void;
  showDesktopViewBreakpoint?: string;
  hideBackButton?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideProgressStepperAtLTEDesktopViewBreakpoint?: boolean;
  hideStepIndex?: boolean;
  footerContent?: React.ReactNode;
  isNarrowLayout?: boolean;
  backgroundColor?: string;
  displayPremiumTopOfFunnelHeader?: boolean;
  footer?: React.ReactNode;
};

const PageWizard = ({
  children,
  description = '',
  isTransitioning = false,
  wizardTitle,
  subtitle = null,
  title = '',
  progressStepper,
  totalSteps = null,
  currentStepIndex,
  currentStepKey = '',
  isBackDisabled = false,
  isNextDisabled = false,
  isNextLoading = false,
  nextButtonType = 'primary',
  nextLabel = 'Next',
  backLabel = 'Back',
  exitCTALabel = 'Exit',
  hideDesktopWizardTitle = false,
  hideExitCTA = false,
  onBackClick = () => {},
  onNextClick = () => {},
  onNextDataEventId,
  onExitTriggered = () => {},
  showDesktopViewBreakpoint = 'md',
  hideBackButton = false,
  hideHeader = false,
  hideFooter = false,
  hideProgressStepperAtLTEDesktopViewBreakpoint = false,
  hideStepIndex = false,
  footerContent = null,
  isNarrowLayout = false,
  backgroundColor,
  displayPremiumTopOfFunnelHeader = false,
  footer = null,
}: PageWizardProps) => {
  const dispatch = useDispatch();

  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const {
    properties: {
      currentProperty,
      currentProperty: {
        listingAlias,
        freemium: {
          activeProduct: { type: activeProductType },
        },
      },
    },
    user,
  } = useSelector((state) => state);

  const { freemiumState } = useContext(FreemiumContext);
  const { canAccessTopOfFunnelPremium } = freemiumState;
  const { reducerDispatch } = useContext(ModularListingContext);

  const displayPremiumPurchaseModal = (isOpen: boolean) => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: isOpen,
    });

    ModularListingCSEvents.ClickUpgradeToPremiumFromHeader({
      currentProperty,
      user,
    });
  };

  const renderPremiumTopOfFunnelHeader = () => {
    return (
      <Spacer>
        <Flex
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginY={{ default: 'sm', md: 'md' }}
          paddingX={{
            default: 'sm',
            md: 'md',
            [showDesktopViewBreakpoint]: 13,
          }}
        >
          <Flex display={isScreenMobile ? 'inline' : 'flex'} alignItems="center">
            {activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <StyledSpan>
                <StyledIconAwardRibbon fontColor="brand" size="xs" />
                <Text textStyle="body-sm-bold" css={{ color: 'blue.400' }} asChild>
                  <span> Premium</span>
                </Text>
              </StyledSpan>
            )}
            <StyledWizardTitle
              hideDesktopWizardLabel={
                hideDesktopWizardTitle || (isScreenMobile && activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM)
              }
            >
              <Text textStyle="heading-xs" css={{ marginInline: '400' }} data-testid="desktop-wizard-title" asChild>
                <span>{wizardTitle}</span>
              </Text>
              {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
                <Text css={{ display: { mdDown: 'none' } }} asChild>
                  <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
                </Text>
              )}
            </StyledWizardTitle>
          </Flex>
          <Flex display="flex" alignItems="center">
            {wizardTitle !== 'Publish' && activeProductType !== PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <TextButton
                onClick={() => displayPremiumPurchaseModal(true)}
                css={{ marginRight: '800', display: { base: 'none', lg: 'inherit' } }}
                data-eventid={ModularListingCSEvents.ClickUpgradeToPremiumFromHeader.data_event_id}
                icon={<IconAwardRibbonFilled />}
                iconPosition="end"
              >
                Upgrade listing to premium
              </TextButton>
            )}
            <StyledExitCTA hideExitCTA={hideExitCTA}>
              <Button
                tone="neutral"
                emphasis="outlined"
                type="button"
                onClick={onExitTriggered}
                data-testid="desktop-exit-button"
              >
                {exitCTALabel}
              </Button>
            </StyledExitCTA>
          </Flex>
        </Flex>
        {progressStepper && (
          <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" alignItems="center">
            <StyledMobileGrid
              showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              hideProgressStepperAtLTEDesktopViewBreakpoint={hideProgressStepperAtLTEDesktopViewBreakpoint}
              gridColumnEnd="span 12"
              paddingTop="md"
              paddingBottom="xs"
            >
              {progressStepper}
            </StyledMobileGrid>
          </Grid>
        )}
      </Spacer>
    );
  };

  const renderHeader = () => {
    const progressStepperDesktopBreakpoint = showDesktopViewBreakpoint.concat('_gte');
    const progressStepperGridBreakpoints = { default: 'span 12' };
    // @ts-expect-error
    progressStepperGridBreakpoints[progressStepperDesktopBreakpoint] = 'span 8';

    const progressStepperPaddingTopBreakpoints = { default: 'sm' };
    // @ts-expect-error
    progressStepperPaddingTopBreakpoints[showDesktopViewBreakpoint] = 0;

    const progressStepperPaddingBottomBreakpoints = { default: 'sm' };
    // @ts-expect-error
    progressStepperPaddingBottomBreakpoints[showDesktopViewBreakpoint] = 'md';

    const headerHorizontalPaddingBreakpoints = {};
    // @ts-expect-error
    headerHorizontalPaddingBreakpoints[showDesktopViewBreakpoint] = 13;
    // @ts-expect-error
    headerHorizontalPaddingBreakpoints[showDesktopViewBreakpoint] = 13;
    // @ts-expect-error
    headerHorizontalPaddingBreakpoints.md = 'md';
    // @ts-expect-error
    headerHorizontalPaddingBreakpoints.xs = 'sm';

    if (canAccessTopOfFunnelPremium && displayPremiumTopOfFunnelHeader) {
      return renderPremiumTopOfFunnelHeader();
    }

    return (
      <Spacer>
        <Flex
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginY={{ default: 'sm', md: 'md' }}
          paddingX={{
            default: 'sm',
            md: 'md',
            [showDesktopViewBreakpoint]: 13,
          }}
        >
          <StyledWizardTitle hideDesktopWizardLabel={hideDesktopWizardTitle}>
            <Text textStyle="heading-xs" css={{ marginInline: '400' }} data-testid="desktop-wizard-title" asChild>
              <span>{wizardTitle}</span>
            </Text>
            {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
              <Text css={{ display: { mdDown: 'none' } }} asChild>
                <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
              </Text>
            )}
          </StyledWizardTitle>
          <StyledExitCTA hideExitCTA={hideExitCTA}>
            <Button
              tone="neutral"
              emphasis="outlined"
              type="button"
              onClick={onExitTriggered}
              data-testid="desktop-exit-button"
            >
              {exitCTALabel}
            </Button>
          </StyledExitCTA>
        </Flex>
        {progressStepper && (
          <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" alignItems="center">
            <StyledMobileGrid
              showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              hideProgressStepperAtLTEDesktopViewBreakpoint={hideProgressStepperAtLTEDesktopViewBreakpoint}
              gridColumnEnd="span 12"
              paddingTop="md"
              paddingBottom="xs"
            >
              {progressStepper}
            </StyledMobileGrid>
          </Grid>
        )}
      </Spacer>
    );
  };

  const renderContent = () => (
    <>
      {title && (
        <Paragraph
          data-testid="title"
          css={{
            textStyle: { base: 'heading-xs', lg: 'heading-md' },
            marginBottom: '400',
          }}
        >
          {title}
        </Paragraph>
      )}
      {subtitle && (
        <Paragraph textStyle="body" data-testid="subtitle" css={{ marginBottom: '400' }}>
          {subtitle}
        </Paragraph>
      )}
      {description && (
        <Paragraph textStyle="body" data-testid="description">
          {description}
        </Paragraph>
      )}
      {children}
    </>
  );

  const renderFooter = () => {
    if (footer) {
      return footer;
    }

    return (
      <FormActions
        data-testid="PageWizard-Footer"
        css={{
          marginBlockStart: '0px !important',
          lgDown: {
            borderTop: '1px solid {colors.border.subtle}',
            borderBottom: '1px solid {colors.border.subtle}',
            position: 'fixed',
            width: '100%',
            bottom: 0,
            background: 'white',
            zIndex: '1',
          },
        }}
      >
        <Spacer marginX="md" marginBottom="md">
          {footerContent}
        </Spacer>

        <Flex
          display="flex"
          justifyContent={hideBackButton ? 'flex-end' : 'space-between'}
          marginY={{ default: 'sm', md: 'md' }}
          marginX={{
            default: 'sm',
            md: 'md',
            xl: 13,
          }}
        >
          {!hideBackButton && (
            <Button
              data-testid="back-button"
              fluid={true}
              disabled={isBackDisabled}
              onClick={onBackClick}
              type="button"
              tone="brand"
              emphasis="outlined"
              css={{
                flexGrow: 1,
                lg: {
                  maxWidth: '160px',
                  flexGrow: 0,
                  marginLeft: 0,
                },
              }}
            >
              {backLabel}
            </Button>
          )}

          <Button
            tone={nextButtonType === 'primary' ? 'brand' : 'neutral'}
            emphasis={nextButtonType === 'primary' ? 'filled' : 'outlined'}
            fluid={true}
            onClick={onNextClick}
            disabled={isNextDisabled}
            data-testid="next-button"
            type="submit"
            loading={isNextLoading}
            data-eventid={onNextDataEventId}
            css={{
              flexGrow: 1,
              marginLeft: '400',
              lg: {
                maxWidth: '160px',
                flexGrow: 0,
                marginLeft: 0,
              },
            }}
          >
            {nextLabel}
          </Button>
        </Flex>
      </FormActions>
    );
  };

  return (
    <StyledWizardContainer display="flex" flexDirection="column">
      <RestrictedListingBanner />
      {!hideHeader && renderHeader()}
      {isTransitioning ? (
        <TransitionWrapper isTransitioning={true} />
      ) : (
        <Form
          asChild={true}
          onSubmit={(e: React.FormEvent) => e.preventDefault()}
          css={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}
        >
          <div>
            <StyledContentContainer backgroundColor={backgroundColor} hideFooter={hideFooter}>
              {progressStepper && (
                <StyledDesktopSpacer
                  showDesktopViewBreakpoint={showDesktopViewBreakpoint}
                  marginTop="md"
                  marginBottom={7}
                >
                  {progressStepper}
                </StyledDesktopSpacer>
              )}
              <Grid
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                marginTop={{ default: 'lg', xxl: 'xl' }}
                marginBottom={{ default: 10, xxl: 10 }}
                className="content-grid"
              >
                <Grid
                  className="grid-col-1"
                  gridColumnEnd={{
                    default: 'span 0',
                    lg: isNarrowLayout ? 'span 2' : 'span 1',
                  }}
                />
                <Grid
                  className="grid-col-2"
                  gridColumnEnd={{
                    default: 'span 12',
                    lg: isNarrowLayout ? 'span 8' : 'span 10',
                  }}
                  justifySelf="inherit"
                >
                  {renderContent()}
                </Grid>
                <Grid
                  className="grid-col-3"
                  gridColumnEnd={{
                    default: 'span 0',
                    lg: isNarrowLayout ? 'span 2' : 'span 1',
                  }}
                />
              </Grid>
            </StyledContentContainer>
            {!hideFooter && renderFooter()}
          </div>
        </Form>
      )}
    </StyledWizardContainer>
  );
};

export default PageWizard;
